// [PURPOSE]: Synthesize `racialInnateSpell` grant effects from a custom race's
//   grantedSpells entries, so player-authored racial innate spells flow through
//   effectRegistry and become VISIBLE in the spellbook (with their casting
//   ability, frequency, and unlock level) — instead of being stored-but-inert.
// [WHY]: INNATE-SPELL-1. The CustomRaceCreator Granted Spells section (incl. the
//   G7 castingAbility/unlockLevel/frequency selectors) wrote race.grantedSpells
//   that reached NO consumer — racialSpells is a dead field, grantedSpells never
//   reach knownSpells, and nothing registered them as effects. Verified trace:
//   .claude/findings/2026-06-14-innate-spell-consumer-congruence-isc1.md and the
//   build trace .claude/findings/2026-06-14-datamodel-racial-innate-spell-surfacing-ris1.md.
//   Synthesizing at REGISTER-time (mirrors raceProficiencyEffects.js / FIX-1a)
//   fixes every existing saved custom race with ZERO migration — grantedSpells
//   storage shape is unchanged.
// [SHAPE]: { type:'racialInnateSpell', operation:'grant',
//            value:{ name, level, castingAbility, frequency, unlockLevel } }
//   Reader: effectCalculator.getRacialInnateSpells (filters by unlockLevel vs
//   character level, computes the save DC from castingAbility). Display:
//   SpellbookTab "Racial Innate Spells" callout.
// [SCOPE]: surfacing + metadata only this slice. Actual casting from the section
//   (free at-will cast / per-rest use consumption) and per-rest use-tracking are
//   breadcrumbed (master-queue INNATE-SPELL-CAST-FROM-SECTION / -PERREST-RESOURCE).

/**
 * Convert a custom race's grantedSpells array into generic effectRegistry
 * `racialInnateSpell` grant effects. Pure — returns a new array, no side effects.
 *
 * Back-compat: entries without the G7 fields (castingAbility/unlockLevel/frequency)
 * are still emitted; the reader defaults them (no DC / unlockLevel 1 / 'at-will').
 *
 * @param {Array|null} grantedSpells - e.g.
 *   [{ name:'Hellish Rebuke', level:2, castingAbility:'charisma', unlockLevel:3, frequency:'1-per-long-rest' }]
 * @returns {Array} grant effects, e.g.
 *   [{ type:'racialInnateSpell', operation:'grant',
 *      value:{ name:'Hellish Rebuke', level:2, castingAbility:'charisma', unlockLevel:3, frequency:'1-per-long-rest' } }]
 */
export function synthesizeRacialInnateSpellEffects(grantedSpells) {
  if (!Array.isArray(grantedSpells)) return [];
  const effects = [];

  grantedSpells.forEach((spell) => {
    const name = typeof spell?.name === 'string' ? spell.name.trim() : '';
    if (!name) return; // nameless entries are silently skipped (parity with grantedSpell validation)

    const value = { name };
    // Preserve only the fields that are present — the reader defaults the rest.
    if (typeof spell.level === 'number') value.level = spell.level;
    else if (spell.level != null && !isNaN(parseInt(spell.level))) value.level = parseInt(spell.level);
    if (spell.castingAbility) value.castingAbility = spell.castingAbility;
    if (spell.frequency) value.frequency = spell.frequency;
    if (spell.unlockLevel != null && !isNaN(parseInt(spell.unlockLevel))) {
      value.unlockLevel = parseInt(spell.unlockLevel);
    }

    effects.push({ type: 'racialInnateSpell', operation: 'grant', value });
  });

  return effects;
}

export default synthesizeRacialInnateSpellEffects;
